import React, { useEffect, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { MetasTablero, SemanasTablero } from "../../../services/v2/prospects.service";
import { errorTexto } from "./salesHelpers";
import { logoDe } from "./tableroView";
import { deIso, iso, rangoTexto } from "./tableroEquipos";

interface Props { campusId: number; mes: string; onGuardado: () => void; }

const PAIS: { [k: string]: string } = { hn: "Honduras", mx: "México" };
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const mesTexto = (m: string) => { const [y, mm] = m.split("-").map(Number); return MESES[mm - 1] + " " + y; };
const sumaDias = (d: string, n: number) => { const x = deIso(d); return iso(new Date(x.getFullYear(), x.getMonth(), x.getDate() + n)); };
const largo = (desde: string, hasta: string) => Math.round((deIso(hasta).getTime() - deIso(desde).getTime()) / 86400000) + 1;

/**
 * Semanas de meta del mes (Esteban, 4-nov-2026): cada mes sale solo con la regla (4 semanas de 7 días; los días que sobran van
 * a la 1, la 3 y la 4, en ese orden) y Dirección o Super Admin lo ajustan aquí. Se edita
 * el día en que cierra cada semana; la siguiente empieza al día siguiente. La primera empieza el 1 y la última termina el último día.
 */
const SemanasMeta: React.FC<{ campusId: number; mes: string; onGuardado: () => void }> = ({ campusId, mes, onGuardado }) => {
	const http = HttpManager.getInstance();
	const [datos, setDatos] = useState<SemanasTablero | null>(null);
	const [cierres, setCierres] = useState<string[]>([]);   // "hasta" de cada semana (la última siempre es el fin de mes)
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const primero = mes + "-01";
	const ultimo = iso(new Date(Number(mes.slice(0, 4)), Number(mes.slice(5, 7)), 0));

	const poner = (d: SemanasTablero) => { setDatos(d); setCierres(d.semanas.map((s) => s.hasta)); };
	useEffect(() => {
		setDatos(null); setError(null); setAviso(null);
		http.prospectsService.tableroSemanas(campusId, mes).then((r) => poner(r.data)).catch((e) => setError(errorTexto(e)));
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [campusId, mes]);

	const semanas = cierres.map((h, i) => ({ n: i + 1, desde: i === 0 ? primero : sumaDias(cierres[i - 1], 1), hasta: h }));
	const cambiado = !!datos && cierres.join() !== datos.semanas.map((s) => s.hasta).join();
	const invalido = semanas.some((s) => s.desde > s.hasta || s.hasta > ultimo) || cierres[cierres.length - 1] !== ultimo;
	const guardar = (lista: { desde: string; hasta: string }[]) => {
		setBusy(true); setError(null); setAviso(null);
		http.prospectsService.guardarSemanas(campusId, mes, lista)
			.then((r) => { poner(r.data); setAviso(lista.length ? "Semanas guardadas." : "Volvió a la regla automática."); onGuardado(); })
			.catch((e) => setError(errorTexto(e)))
			.finally(() => setBusy(false));
	};
	const partir = () => {
		// La última semana se parte a la mitad.
		const u = semanas[semanas.length - 1];
		if (!u || largo(u.desde, u.hasta) < 2) return;
		const medio = sumaDias(u.desde, Math.floor(largo(u.desde, u.hasta) / 2) - 1);
		setCierres([...cierres.slice(0, -1), medio, ultimo]);
	};
	const quitar = (i: number) => setCierres(cierres.filter((_, j) => j !== i));

	if (error && !datos) return <div className="aviso error">{error}</div>;
	if (!datos) return <div className="ventas-vacio">Cargando…</div>;
	return (
		<div className="tarjeta tb-ventas">
			<div className="tarjeta-cab">
				<h4>Semanas de meta</h4>
				<span className="apagado">{mesTexto(mes)} · {datos.automatico ? "regla automática" : "ajustadas por Dirección"}{datos.puedeEditar ? "" : " · solo Dirección las cambia"}</span>
			</div>
			<div className="waScrollX">
				<table className="tb-tabla tb-semanas">
					<thead><tr><th>Semana</th><th>Desde</th><th>Cierra</th><th>Días</th>{datos.puedeEditar ? <th /> : null}</tr></thead>
					<tbody>
						{semanas.map((s, i) => {
							const ultima = i === semanas.length - 1;
							return (
								<tr key={i} className={datos.semanas[i]?.hasta !== s.hasta ? "tb-meta-cambiada" : ""}>
									<td><b>Semana {s.n}</b><span className="apagado" style={{ marginLeft: 8, fontSize: 12 }}>{s.desde <= s.hasta ? rangoTexto(s.desde, s.hasta) : "—"}</span></td>
									<td className="apagado">{Number(s.desde.slice(8))}</td>
									<td>
										{datos.puedeEditar && !ultima
											? <input type="date" value={s.hasta} min={s.desde} max={sumaDias(ultimo, -1)} disabled={busy} aria-label={"Cierre de la semana " + s.n}
												onChange={(ev) => { if (ev.target.value) setCierres(cierres.map((c, j) => (j === i ? ev.target.value : c))); }} />
											: <b>{Number(s.hasta.slice(8))}</b>}
									</td>
									<td className="apagado">{s.desde <= s.hasta ? largo(s.desde, s.hasta) : "—"}</td>
									{datos.puedeEditar ? <td>{i > 0 ? <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => quitar(i)}>Unir con la anterior</button> : null}</td> : null}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			{error ? <div className="aviso error">{error}</div> : null}
			{aviso ? <div className="aviso">{aviso}</div> : null}
			{datos.puedeEditar ? (
				<div className="tb-editar-botones">
					<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy || !cambiado || invalido} onClick={() => guardar(semanas.map((s) => ({ desde: s.desde, hasta: s.hasta })))}>Guardar semanas</button>
					<button type="button" className="btn btn-outline-primary btn-sm rounded-pill" disabled={busy || semanas.length >= 8} onClick={partir}>Partir la última</button>
					{cambiado ? <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => setCierres(datos.semanas.map((s) => s.hasta))}>Deshacer</button> : null}
					{!datos.automatico ? <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => guardar([])}>Volver a la regla automática</button> : null}
				</div>
			) : null}
		</div>
	);
};

/**
 * Metas por equipo (Esteban, 2026-11-04: "haz la pantalla"): Dirección y Super Admin las cambian aquí; los demás solo las ven.
 * Una meta por equipo y mes (la misma todos los meses hasta que la cambien). Logo, color y sonido no se tocan desde aquí.
 */
const TableroMetas: React.FC<Props> = ({ campusId, mes, onGuardado }) => {
	const http = HttpManager.getInstance();
	const [datos, setDatos] = useState<MetasTablero | null>(null);
	const [metas, setMetas] = useState<{ [k: string]: string }>({});
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	const cargar = () => http.prospectsService.tableroEquipos(campusId).then((r) => {
		setDatos(r.data);
		const m: { [k: string]: string } = {};
		r.data.equipos.forEach((e) => { m[e.pais + "|" + e.equipo] = String(e.meta); });
		setMetas(m);
	}).catch((e) => setError(errorTexto(e)));
	useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campusId]);

	const cambiados = datos ? datos.equipos.filter((e) => Number(metas[e.pais + "|" + e.equipo]) !== e.meta) : [];
	const invalidos = datos ? datos.equipos.filter((e) => { const v = Number(metas[e.pais + "|" + e.equipo]); return !(v >= 0 && v <= 9999); }) : [];

	const guardar = () => {
		if (!datos) return;
		setBusy(true); setError(null); setAviso(null);
		http.prospectsService.guardarMetas(campusId, cambiados.map((e) => ({ pais: e.pais, equipo: e.equipo, meta: Number(metas[e.pais + "|" + e.equipo]) })))
			.then((r) => {
				setDatos(r.data);
				const m: { [k: string]: string } = {};
				r.data.equipos.forEach((e) => { m[e.pais + "|" + e.equipo] = String(e.meta); });
				setMetas(m);
				setAviso(cambiados.length === 1 ? "Meta guardada." : cambiados.length + " metas guardadas.");
				onGuardado();
			})
			.catch((e) => setError(errorTexto(e)))
			.finally(() => setBusy(false));
	};

	if (error && !datos) return <div className="aviso error">{error}</div>;
	if (!datos) return <div className="ventas-vacio">Cargando…</div>;
	return (
		<div className="tb-metas-dos">
		<div className="tarjeta tb-ventas">
			<div className="tarjeta-cab">
				<h4>Metas por equipo</h4>
				<span className="apagado">Matrículas del mes{datos.puedeEditar ? "" : " · solo Dirección las cambia"}</span>
			</div>
			<div className="waScrollX">
				<table className="tb-tabla tb-metas">
					<thead><tr><th>Equipo</th><th>País</th><th>Meta</th></tr></thead>
					<tbody>
						{datos.equipos.map((e) => {
							const k = e.pais + "|" + e.equipo;
							const cambio = Number(metas[k]) !== e.meta;
							return (
								<tr key={k} className={cambio ? "tb-meta-cambiada" : ""}>
									<td><span className="tb-meta-eq" style={{ "--col": e.color || "var(--wa-apagado)" } as React.CSSProperties}><img src={logoDe(e)} alt="" />{e.equipo}</span></td>
									<td className="apagado">{PAIS[e.pais] || e.pais}</td>
									<td>
										{datos.puedeEditar
											? <input type="number" min={0} max={9999} step={1} value={metas[k] ?? ""} disabled={busy} aria-label={"Meta de " + e.equipo}
												onChange={(ev) => setMetas({ ...metas, [k]: ev.target.value })} style={{ width: 96 }} />
											: <b>{e.meta}</b>}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			{error ? <div className="aviso error">{error}</div> : null}
			{aviso ? <div className="aviso">{aviso}</div> : null}
			{datos.puedeEditar ? (
				<div className="tb-editar-botones">
					<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy || !cambiados.length || invalidos.length > 0} onClick={guardar}>
						{cambiados.length > 1 ? "Guardar " + cambiados.length + " metas" : "Guardar"}
					</button>
					{cambiados.length ? <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => { const m: { [k: string]: string } = {}; datos.equipos.forEach((e) => { m[e.pais + "|" + e.equipo] = String(e.meta); }); setMetas(m); }}>Deshacer</button> : null}
				</div>
			) : null}
		</div>
		<SemanasMeta campusId={campusId} mes={mes} onGuardado={onGuardado} />
		</div>
	);
};

export default TableroMetas;
